import * as React from 'react';
import * as lo from 'lodash/fp';
import styled from 'styled-components';

import { NumberInput } from '@process-advisor/process-miner/model/statistics/numberInput';
import { SingleBlock as SB } from '@process-advisor/process-miner/components/layout/singleBlock';
import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';
import { getShadow, getZIndex, getColor, getFontSize } from '@process-advisor/process-miner/shared/theme';
import { Event } from '@process-advisor/process-miner/api/events';
import { SidebarLoaderContext, type SidebarLoaderContextType } from '@process-advisor/process-miner/shared/providers/sidebarLoaderProvider';
import { saveModel } from '@process-advisor/process-miner/processMiner/processMiner';

export const SingleBlock = styled(SB)`
    align-items: center;
`;

const Wrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    box-shadow: ${getShadow('pane')};
    z-index: ${getZIndex('panel')};
`;

const LabelRight = styled.div`
    font-size: ${getFontSize('smaller')};
    color: ${getColor('blue')};
    margin-top: 12px;
    text-align: right;
    width: 100%;
`;
const LabelCenter = styled.div`
    font-size: ${getFontSize('smaller')};
    color: ${getColor('black')};
    margin-top: 12px;
    text-align: center;
    width: 100%;
`;

export type Row = {
    label: string;
    events: number;
    tasks: number;
    variants: number;
    activities: number;
    steps: number;
};

export const Statistics = () => {
    const { setProps } = React.useContext<SidebarLoaderContextType>(SidebarLoaderContext);
    const [data, setData] = React.useState<Row[]>([]);

    React.useEffect(() => {
        setData(apiProvider.modelInfo());
    }, []);

    React.useEffect(() => {
        const listener = () =>
            setData(apiProvider.modelInfo());
        listener();

        apiProvider.on(Event.applyFilter, listener);
        apiProvider.on(Event.resetFilter, listener);
        apiProvider.on(Event.updateGaugeMetrics, listener);
        apiProvider.on(Event.updateVariantSlicerVariantChart, listener);

        return () => {
            apiProvider.off(Event.applyFilter, listener);
            apiProvider.off(Event.resetFilter, listener);
            apiProvider.off(Event.updateGaugeMetrics, listener);
            apiProvider.off(Event.updateVariantSlicerVariantChart, listener);
        };
    }, []);

    const resetModel = () => {
        setProps({
            functionToLoad: () => reset(),
        });
    }

    function reset () {
        apiProvider.resetModel()
        apiProvider.resetFilters({processMiner: true, regenerateArray: true})
        apiProvider.updateGauges({});
        apiProvider.updateMetricSettings({})
    }

    const onSave = async () => {
        // Call the centralized saveModel from processMiner
        await saveModel('FORGE'); // Forge version
        apiProvider.alertMessage({
            message: 'Model Saved',
            severity: 'success',
            showAlert: true,
            timeOutActive: true,
            component: 'upload_file',
        });  
    }




    if (lo.isEmpty(data)) {
        return null;
    }
    const entityLabel = apiProvider.getEntityUpper() + 's'

    return (
        <Wrapper>
            <SingleBlock width="15%">
                <LabelCenter>{''}</LabelCenter>
            </SingleBlock>
            <SingleBlock width="17%">
                <LabelCenter>{'Events'}</LabelCenter>
            </SingleBlock>
            <SingleBlock width="17%">
                <LabelCenter>{entityLabel}</LabelCenter>
            </SingleBlock>
            <SingleBlock width="17%">
                <LabelCenter>{'Variants'}</LabelCenter>
            </SingleBlock>
            <SingleBlock width="17%">
                <LabelCenter>{'Steps'}</LabelCenter>
            </SingleBlock>
            <SingleBlock width="17%">
                <LabelCenter>{'Activities'}</LabelCenter>
            </SingleBlock>
            <SingleBlock width="15%">
                <LabelRight>{'Total:'}</LabelRight>
            </SingleBlock>
            <SingleBlock width="17%">
                <NumberInput
                    value={data[0].events}/>
            </SingleBlock>
            <SingleBlock width="17%">
                <NumberInput
                    value={data[0].tasks}/>
            </SingleBlock>
            <SingleBlock width="17%">
                <NumberInput
                    value={data[0].variants}/>
            </SingleBlock>
            <SingleBlock width="17%">
                <NumberInput
                    value={data[0].steps}/>
            </SingleBlock>
            <SingleBlock width="17%">
                <NumberInput
                    value={data[0].activities}/>
            </SingleBlock>
            <SingleBlock width="15%">
                <LabelRight>{'Filtered:'}</LabelRight>
            </SingleBlock>
            <SingleBlock width="17%">
                <NumberInput
                    value={data[1].events}/>
            </SingleBlock>
            <SingleBlock width="17%">
                <NumberInput
                    value={data[1].tasks}/>
            </SingleBlock>
            <SingleBlock width="17%">
                <NumberInput
                    value={data[1].variants}/>
            </SingleBlock>
            <SingleBlock width="17%">
                <NumberInput
                    value={data[1].steps}/>
            </SingleBlock>
            <SingleBlock width="17%">
                <NumberInput
                    value={data[1].activities}/>
            </SingleBlock>
            <SingleBlock width="15%">
                <LabelRight>{'Selected:'}</LabelRight>
            </SingleBlock>
            <SingleBlock width="17%">
                <NumberInput
                    value={data[2].events}/>
            </SingleBlock>
            <SingleBlock width="17%">
                <NumberInput
                    value={data[2].tasks}/>
            </SingleBlock>
            <SingleBlock width="17%">
                <NumberInput
                    value={data[2].variants}/>
            </SingleBlock>
            <SingleBlock width="17%">
                <NumberInput
                    value={data[2].steps}/>
            </SingleBlock>
            <SingleBlock width="17%">
                <NumberInput
                    value={data[2].activities}/>
            </SingleBlock>

        </Wrapper>

    );
};
